// WHAT: Calendar Page Component
// WHY:  Main calendar view with event management (placeholder/static implementation)
// DOMAIN: Productivity → Calendar
// DEPENDENCIES: React
// RISKS:
//   - NOTE: This is a static placeholder - no actual calendar functionality
//   - NOTE: Hardcoded date (January 2025) and events
//   - NOTE: Navigation buttons and form inputs have no functionality
//   - NOTE: Calendar grid shows fixed 31 days (not month-aware)

import React from "react";

/**
 * WHAT:
 *   Calendar page component with static placeholder UI.
 *
 * WHY:
 *   Provides calendar view structure for future implementation.
 *   Currently displays static data and non-functional UI elements.
 *
 * BEHAVIOR:
 *   - Displays calendar grid with week days and 31 days
 *   - Shows hardcoded month (January 2025)
 *   - Displays static event list (Today's Events, Upcoming)
 *   - Shows non-functional navigation buttons (prev/next month)
 *   - Displays non-functional quick add form
 *   - Highlights day 15 as selected (hardcoded)
 *
 * RISKS:
 *   - WARN: This is a placeholder - no actual calendar functionality
 *   - NOTE: Hardcoded date and events (not dynamic)
 *   - NOTE: Navigation buttons don't work
 *   - NOTE: Form inputs don't submit or save data
 *   - NOTE: Calendar grid always shows 31 days (not month-aware)
 *
 * @returns CalendarPage component
 *
 */
export const CalendarPage: React.FC = () => {
  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Calendar</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar View */}
          <div className="lg:col-span-2 bg-neutral-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">January 2025</h2>
              <div className="flex items-center gap-2">
                <button className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors">
                  ←
                </button>
                <button className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors">
                  →
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Week days */}
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div className="p-2 text-center text-sm font-medium text-neutral-400" key={day}>
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                <div
                  className={`p-2 text-center text-sm rounded-lg cursor-pointer transition-colors ${
                    day === 15 ? "bg-blue-500 text-white" : "text-neutral-300 hover:bg-neutral-700"
                  }`}
                  key={day}
                >
                  {day}
                </div>
              ))}
            </div>
          </div>

          {/* Events Sidebar */}
          <div className="space-y-6">
            {/* Today's Events */}
            <div className="bg-neutral-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Today&apos;s Events</h3>
              <div className="space-y-3">
                {[
                  { time: "09:00", title: "Morning Standup", type: "meeting" },
                  { time: "12:00", title: "Lunch with Team", type: "personal" },
                  { time: "15:00", title: "Client Call", type: "meeting" },
                  { time: "18:00", title: "Gym Session", type: "personal" },
                ].map((event) => (
                  <div
                    className="flex items-center gap-3 p-3 bg-neutral-700/50 rounded-lg"
                    key={`${event.time}-${event.title}`}
                  >
                    <div className="text-2xl">{event.type === "meeting" ? "👥" : "💪"}</div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{event.title}</p>
                      <p className="text-sm text-neutral-400">{event.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Add */}
            <div className="bg-neutral-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quick Add</h3>
              <div className="space-y-3">
                <input
                  className="w-full p-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-blue-500"
                  placeholder="Event title"
                  type="text"
                />
                <input
                  className="w-full p-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  type="datetime-local"
                />
                <select className="w-full p-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-blue-500">
                  <option value="meeting">Meeting</option>
                  <option value="personal">Personal</option>
                  <option value="appointment">Appointment</option>
                </select>
                <button className="w-full p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                  Add Event
                </button>
              </div>
            </div>

            {/* Upcoming */}
            <div className="bg-neutral-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Upcoming</h3>
              <div className="space-y-3">
                {[
                  {
                    date: "Tomorrow",
                    title: "Project Review",
                    type: "meeting",
                  },
                  {
                    date: "Jan 18",
                    title: "Dentist Appointment",
                    type: "appointment",
                  },
                  { date: "Jan 20", title: "Team Dinner", type: "personal" },
                ].map((event) => (
                  <div
                    className="flex items-center gap-3 p-3 bg-neutral-700/50 rounded-lg"
                    key={`${event.date}-${event.title}`}
                  >
                    <div className="text-2xl">
                      {(() => {
                        if (event.type === "meeting") {
                          return "👥";
                        }
                        if (event.type === "appointment") {
                          return "🏥";
                        }
                        return "🍽️";
                      })()}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{event.title}</p>
                      <p className="text-sm text-neutral-400">{event.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
